<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import cortexSettingsAPI from 'dashboard/api/cortexSettings';
import cortexModelRoutesAPI from 'dashboard/api/cortexModelRoutes';
import NextButton from 'dashboard/components-next/button/Button.vue';
import NextInput from 'dashboard/components-next/input/Input.vue';
import AdaptiveSelect from 'dashboard/components-next/adaptive-select/AdaptiveSelect.vue';
import NextSwitch from 'dashboard/components-next/switch/Switch.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Label from 'dashboard/components-next/label/Label.vue';
import WithLabel from 'v3/components/Form/WithLabel.vue';

const { t } = useI18n();
const WORKLOADS = [
  'realtime_response',
  'background',
  'media',
  'transcription',
  'embeddings',
  'decision',
];
const routes = ref([]);
const connections = ref([]);
const modelOptionsByProvider = ref({});
const embeddingOptionsByProvider = ref({});
const expandedWorkload = ref(null);
const isLoading = ref(false);
const savingWorkload = ref(null);
const revertDialog = ref(null);
const pendingRevert = ref(null);

const strategyOptions = computed(() => [
  {
    value: 'priority',
    label: t('CORTEX_MODELS.ROUTES.STRATEGIES.priority'),
    description: t('CORTEX_MODELS.ROUTES.STRATEGY_DESCRIPTIONS.priority'),
  },
  {
    value: 'weighted',
    label: t('CORTEX_MODELS.ROUTES.STRATEGIES.weighted'),
    description: t('CORTEX_MODELS.ROUTES.STRATEGY_DESCRIPTIONS.weighted'),
  },
]);
const workloadMeta = computed(() => ({
  realtime_response: {
    title: t('CORTEX_MODELS.ROUTES.WORKLOADS.realtime_response.TITLE'),
    description: t(
      'CORTEX_MODELS.ROUTES.WORKLOADS.realtime_response.DESCRIPTION'
    ),
  },
  background: {
    title: t('CORTEX_MODELS.ROUTES.WORKLOADS.background.TITLE'),
    description: t('CORTEX_MODELS.ROUTES.WORKLOADS.background.DESCRIPTION'),
  },
  media: {
    title: t('CORTEX_MODELS.ROUTES.WORKLOADS.media.TITLE'),
    description: t('CORTEX_MODELS.ROUTES.WORKLOADS.media.DESCRIPTION'),
  },
  transcription: {
    title: t('CORTEX_MODELS.ROUTES.WORKLOADS.transcription.TITLE'),
    description: t('CORTEX_MODELS.ROUTES.WORKLOADS.transcription.DESCRIPTION'),
  },
  embeddings: {
    title: t('CORTEX_MODELS.ROUTES.WORKLOADS.embeddings.TITLE'),
    description: t('CORTEX_MODELS.ROUTES.WORKLOADS.embeddings.DESCRIPTION'),
  },
  decision: {
    title: t('CORTEX_MODELS.ROUTES.WORKLOADS.decision.TITLE'),
    description: t('CORTEX_MODELS.ROUTES.WORKLOADS.decision.DESCRIPTION'),
  },
}));

const isRouteReady = route =>
  route.status === 'active' && route.targets.length > 0;
const routeStatusColor = route => (isRouteReady(route) ? 'teal' : 'slate');
const routeStatusLabel = route =>
  isRouteReady(route)
    ? t('CORTEX_MODELS.CONNECTIONS.STATUS_ACTIVE')
    : t('CORTEX_MODELS.CONNECTIONS.STATUS_INACTIVE');

const activeConnections = computed(() =>
  connections.value.filter(
    connection =>
      connection.status === 'active' &&
      connection.credentials.some(credential => credential.status === 'active')
  )
);
const connectionOptions = computed(() =>
  connections.value.map(connection => ({
    value: connection.id,
    label: `${connection.name} · ${connection.provider_name}`,
    disabled: !activeConnections.value.some(item => item.id === connection.id),
  }))
);

const routeTimeout = workload => {
  if (workload === 'realtime_response') return 30;
  if (workload === 'decision') return 15;
  return 120;
};

const defaultRoute = workload => ({
  id: null,
  account_id: null,
  owner_scope: 'account',
  name: workloadMeta.value[workload].title,
  workload,
  strategy: 'priority',
  status: 'active',
  max_attempts: 2,
  timeout_seconds: routeTimeout(workload),
  allow_paid_fallback: false,
  inherited: false,
  targets: [],
});

const loadData = async () => {
  isLoading.value = true;
  try {
    const [{ data: settingsData }, { data: routeData }] = await Promise.all([
      cortexSettingsAPI.get(),
      cortexModelRoutesAPI.get(),
    ]);
    connections.value = settingsData.model?.provider_connections || [];
    modelOptionsByProvider.value =
      settingsData.model?.model_options_by_provider || {};
    embeddingOptionsByProvider.value =
      settingsData.model?.embedding_model_options_by_provider || {};
    routes.value = WORKLOADS.map(workload => {
      const route = routeData.find(item => item.workload === workload);
      return route ? structuredClone(route) : defaultRoute(workload);
    });
  } catch (error) {
    useAlert(t('CORTEX_MODELS.ERRORS.LOAD'));
  } finally {
    isLoading.value = false;
  }
};

const connectionForTarget = target =>
  connections.value.find(
    connection => connection.id === Number(target.provider_connection_id)
  );

const modelOptionsFor = (route, target) => {
  const connection = connectionForTarget(target);
  if (!connection) return [];
  const source =
    route.workload === 'embeddings'
      ? embeddingOptionsByProvider.value
      : modelOptionsByProvider.value;
  const options =
    route.workload === 'embeddings'
      ? source[connection.provider] || []
      : connection.model_options || source[connection.provider] || [];
  return options.map(option => ({
    value: option.value,
    label: option.name || option.label,
  }));
};

const setTargetConnection = (route, target, connectionId) => {
  target.provider_connection_id = connectionId;
  target.model = modelOptionsFor(route, target)[0]?.value || '';
};

const addTarget = route => {
  const connection = activeConnections.value[0];
  if (!connection) return;
  const target = {
    provider_connection_id: connection.id,
    model: '',
    position: route.targets.length,
    weight: 100,
    status: 'active',
    max_input_cost_usd: null,
    max_output_cost_usd: null,
  };
  route.targets.push(target);
  target.model = modelOptionsFor(route, target)[0]?.value || '';
};

const removeTarget = (route, index) => {
  route.targets.splice(index, 1);
  route.targets.forEach((target, position) => {
    target.position = position;
  });
};

const routePayload = route => ({
  name: route.name,
  workload: route.workload,
  strategy: route.strategy,
  status: route.status,
  max_attempts: Number(route.max_attempts),
  timeout_seconds: Number(route.timeout_seconds),
  allow_paid_fallback: route.allow_paid_fallback,
  targets: route.targets.map((target, position) => ({
    provider_connection_id: Number(target.provider_connection_id),
    model: target.model,
    position,
    weight: Number(target.weight),
    status: target.status,
    max_input_cost_usd: target.max_input_cost_usd || null,
    max_output_cost_usd: target.max_output_cost_usd || null,
  })),
});

const saveRoute = async route => {
  savingWorkload.value = route.workload;
  try {
    const payload = { model_route: routePayload(route) };
    const response =
      route.id && !route.inherited
        ? await cortexModelRoutesAPI.update(route.id, payload)
        : await cortexModelRoutesAPI.create(payload);
    const index = routes.value.findIndex(
      item => item.workload === route.workload
    );
    routes.value[index] = structuredClone(response.data);
    useAlert(t('CORTEX_MODELS.ROUTES.SAVED'));
  } catch (error) {
    useAlert(
      error.response?.data?.errors?.[0] || t('CORTEX_MODELS.ERRORS.SAVE')
    );
  } finally {
    savingWorkload.value = null;
  }
};

const revertRoute = async route => {
  if (!route.id || route.inherited) return;
  try {
    const { data } = await cortexModelRoutesAPI.delete(route.id);
    const index = routes.value.findIndex(
      item => item.workload === route.workload
    );
    routes.value[index] = Object.keys(data).length
      ? structuredClone(data)
      : defaultRoute(route.workload);
    useAlert(t('CORTEX_MODELS.ROUTES.REVERTED'));
  } catch (error) {
    useAlert(
      error.response?.data?.errors?.[0] || t('CORTEX_MODELS.ERRORS.DELETE')
    );
  }
};

const requestRouteRevert = route => {
  pendingRevert.value = route;
  revertDialog.value?.open();
};

const customizeRoute = route => {
  route.id = null;
  route.account_id = null;
  route.owner_scope = 'account';
  route.inherited = false;
};

const confirmRouteRevert = async () => {
  const route = pendingRevert.value;
  if (!route) return;

  await revertRoute(route);
  pendingRevert.value = null;
  revertDialog.value?.close();
};

const strategyOption = strategy =>
  strategyOptions.value.find(option => option.value === strategy);

onMounted(loadData);
</script>

<template>
  <div class="flex flex-col gap-5" :class="{ 'opacity-60': isLoading }">
    <div>
      <h4 class="text-heading-3 text-n-slate-12">
        {{ t('CORTEX_MODELS.ROUTES.TITLE') }}
      </h4>
      <p class="mt-1 text-body-small text-n-slate-11">
        {{ t('CORTEX_MODELS.ROUTES.DESCRIPTION') }}
      </p>
      <p class="mt-1 text-label-small leading-4 text-n-slate-11">
        {{ t('CORTEX_MODELS.ROUTES.INHERITED_DESCRIPTION') }}
      </p>
    </div>

    <div
      v-if="!activeConnections.length && !isLoading"
      class="flex items-start gap-3 p-4 rounded-lg bg-n-alpha-2"
    >
      <span class="i-lucide-circle-alert size-5 mt-0.5 text-n-amber-11" />
      <div class="flex-1 min-w-0">
        <p class="text-body-small text-n-slate-11">
          {{ t('CORTEX_MODELS.ROUTES.NO_CONNECTIONS') }}
        </p>
      </div>
    </div>

    <div class="overflow-hidden border rounded-lg border-n-weak">
      <div
        v-for="route in routes"
        :key="route.workload"
        class="border-b last:border-b-0 border-n-weak"
      >
        <NextButton
          type="button"
          slate
          ghost
          start
          class="!h-auto w-full !rounded-none px-4 py-3 text-left"
          :aria-expanded="expandedWorkload === route.workload"
          @click="
            expandedWorkload =
              expandedWorkload === route.workload ? null : route.workload
          "
        >
          <span class="i-lucide-route size-4 text-n-slate-10" />
          <span class="flex-1 min-w-0">
            <span class="flex items-center gap-2">
              <span class="text-sm font-medium text-n-slate-12">
                {{ workloadMeta[route.workload].title }}
              </span>
              <span v-if="route.inherited" class="text-xs text-n-slate-10">
                {{ t('CORTEX_MODELS.ROUTES.INHERITED') }}
              </span>
            </span>
            <span class="block mt-0.5 text-xs text-n-slate-10">
              {{ route.targets.length }}
              {{ t('CORTEX_MODELS.ROUTES.TARGETS') }}
              {{ t('CORTEX_MODELS.SEPARATOR') }}
              {{ strategyOption(route.strategy)?.label }}
            </span>
          </span>
          <Label
            :label="routeStatusLabel(route)"
            :color="routeStatusColor(route)"
            compact
          >
            <template #icon>
              <span class="i-lucide-circle size-2.5 fill-current" />
            </template>
          </Label>
          <span
            class="i-lucide-chevron-down size-4 text-n-slate-10 transition-transform"
            :class="{ 'rotate-180': expandedWorkload === route.workload }"
          />
        </NextButton>

        <div
          v-if="expandedWorkload === route.workload"
          class="p-4 border-t bg-n-alpha-1 border-n-weak"
        >
          <p class="mb-5 text-body-small text-n-slate-11">
            {{ workloadMeta[route.workload].description }}
          </p>

          <fieldset :disabled="route.inherited" class="contents">
            <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
              <WithLabel
                :name="`route-strategy-${route.workload}`"
                :label="t('CORTEX_MODELS.ROUTES.STRATEGY')"
                :help-message="strategyOption(route.strategy)?.description"
              >
                <AdaptiveSelect
                  v-model="route.strategy"
                  :options="strategyOptions"
                />
              </WithLabel>
              <NextInput
                v-model="route.timeout_seconds"
                type="number"
                min="5"
                max="300"
                :label="t('CORTEX_MODELS.ROUTES.TIMEOUT')"
                :message="t('CORTEX_MODELS.ROUTES.TIMEOUT_DESCRIPTION')"
              />
              <NextInput
                v-model="route.max_attempts"
                type="number"
                min="1"
                max="5"
                :label="t('CORTEX_MODELS.ROUTES.MAX_ATTEMPTS')"
                :message="t('CORTEX_MODELS.ROUTES.MAX_ATTEMPTS_DESCRIPTION')"
              />
              <label class="flex items-start gap-3 pt-1 cursor-pointer">
                <NextSwitch
                  v-model="route.allow_paid_fallback"
                  class="mt-0.5"
                />
                <span>
                  <span class="block text-sm font-medium text-n-slate-12">
                    {{ t('CORTEX_MODELS.ROUTES.ALLOW_PAID') }}
                  </span>
                  <span class="block mt-1 text-body-small text-n-slate-11">
                    {{ t('CORTEX_MODELS.ROUTES.ALLOW_PAID_DESCRIPTION') }}
                  </span>
                </span>
              </label>
              <label class="flex items-start gap-3 pt-1 cursor-pointer">
                <NextSwitch
                  :model-value="route.status === 'active'"
                  class="mt-0.5"
                  @change="route.status = $event ? 'active' : 'inactive'"
                />
                <span>
                  <span class="block text-sm font-medium text-n-slate-12">
                    {{ t('CORTEX_MODELS.ROUTES.ACTIVE') }}
                  </span>
                  <span class="block mt-1 text-body-small text-n-slate-11">
                    {{ t('CORTEX_MODELS.ROUTES.ACTIVE_DESCRIPTION') }}
                  </span>
                </span>
              </label>
            </div>

            <div class="pt-4 mt-5 border-t border-n-weak">
              <h5 class="text-sm font-medium text-n-slate-12">
                {{ t('CORTEX_MODELS.ROUTES.DESTINATIONS') }}
              </h5>
              <p class="mt-1 text-label-small leading-4 text-n-slate-11">
                {{ t('CORTEX_MODELS.ROUTES.DESTINATIONS_DESCRIPTION') }}
              </p>

              <div class="flex flex-col gap-4 mt-4">
                <div
                  v-for="(target, index) in route.targets"
                  :key="target.id || index"
                  class="p-4 border rounded-lg border-n-weak"
                >
                  <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <WithLabel
                      :name="`route-connection-${route.workload}-${index}`"
                      :label="t('CORTEX_MODELS.ROUTES.CONNECTION')"
                      :help-message="
                        t('CORTEX_MODELS.ROUTES.CONNECTION_DESCRIPTION')
                      "
                    >
                      <AdaptiveSelect
                        :model-value="target.provider_connection_id"
                        :options="connectionOptions"
                        @update:model-value="
                          setTargetConnection(route, target, $event)
                        "
                      />
                    </WithLabel>
                    <WithLabel
                      :name="`route-model-${route.workload}-${index}`"
                      :label="t('CORTEX_MODELS.ROUTES.MODEL')"
                      :help-message="
                        t('CORTEX_MODELS.ROUTES.MODEL_DESCRIPTION')
                      "
                    >
                      <AdaptiveSelect
                        v-model="target.model"
                        :options="modelOptionsFor(route, target)"
                      />
                    </WithLabel>
                    <NextInput
                      v-if="route.strategy === 'weighted'"
                      v-model="target.weight"
                      type="number"
                      min="1"
                      max="100"
                      :label="t('CORTEX_MODELS.ROUTES.WEIGHT')"
                      :message="t('CORTEX_MODELS.ROUTES.WEIGHT_DESCRIPTION')"
                    />
                    <label class="flex items-start gap-3 pt-1 cursor-pointer">
                      <NextSwitch
                        :model-value="target.status === 'active'"
                        class="mt-0.5"
                        @change="target.status = $event ? 'active' : 'inactive'"
                      />
                      <span>
                        <span class="block text-sm font-medium text-n-slate-12">
                          {{ t('CORTEX_MODELS.ROUTES.TARGET_ACTIVE') }}
                        </span>
                        <span
                          class="block mt-1 text-body-small text-n-slate-11"
                        >
                          {{
                            t('CORTEX_MODELS.ROUTES.TARGET_ACTIVE_DESCRIPTION')
                          }}
                        </span>
                      </span>
                    </label>
                    <div class="flex items-start justify-end md:pt-6">
                      <NextButton
                        v-if="
                          route.workload !== 'embeddings' ||
                          route.targets.length > 1
                        "
                        :label="t('CORTEX_MODELS.ACTIONS.REMOVE_DESTINATION')"
                        icon="i-lucide-trash-2"
                        ruby
                        ghost
                        @click="removeTarget(route, index)"
                      />
                    </div>
                  </div>

                  <details class="pt-3 mt-3 border-t border-n-weak">
                    <summary
                      class="text-xs font-medium cursor-pointer text-n-slate-11"
                    >
                      {{ t('CORTEX_MODELS.ROUTES.COST_LIMITS') }}
                    </summary>
                    <div class="grid grid-cols-1 gap-4 mt-3 md:grid-cols-2">
                      <NextInput
                        v-model="target.max_input_cost_usd"
                        type="number"
                        min="0"
                        step="0.0001"
                        :label="t('CORTEX_MODELS.ROUTES.MAX_INPUT_COST')"
                        :message="
                          t('CORTEX_MODELS.ROUTES.MAX_INPUT_COST_DESCRIPTION')
                        "
                      />
                      <NextInput
                        v-model="target.max_output_cost_usd"
                        type="number"
                        min="0"
                        step="0.0001"
                        :label="t('CORTEX_MODELS.ROUTES.MAX_OUTPUT_COST')"
                        :message="
                          t('CORTEX_MODELS.ROUTES.MAX_OUTPUT_COST_DESCRIPTION')
                        "
                      />
                    </div>
                  </details>
                </div>
              </div>

              <NextButton
                v-if="route.workload !== 'embeddings' || !route.targets.length"
                :label="t('CORTEX_MODELS.ACTIONS.ADD_DESTINATION')"
                icon="i-lucide-plus"
                outline
                class="mt-4"
                :disabled="!activeConnections.length"
                @click="addTarget(route)"
              />
            </div>
          </fieldset>

          <div
            class="flex justify-between gap-3 pt-5 mt-5 border-t border-n-weak"
          >
            <NextButton
              v-if="route.id && !route.inherited"
              :label="t('CORTEX_MODELS.ACTIONS.REVERT_ROUTE')"
              slate
              ghost
              @click="requestRouteRevert(route)"
            />
            <span v-else />
            <NextButton
              :label="
                route.inherited
                  ? t('CORTEX_MODELS.ACTIONS.CUSTOMIZE_ROUTE')
                  : t('CORTEX_MODELS.ACTIONS.SAVE_ROUTE')
              "
              :icon="route.inherited ? 'i-lucide-copy-plus' : 'i-lucide-save'"
              :is-loading="savingWorkload === route.workload"
              :disabled="!route.targets.length"
              @click="
                route.inherited ? customizeRoute(route) : saveRoute(route)
              "
            />
          </div>
        </div>
      </div>
    </div>

    <Dialog
      ref="revertDialog"
      type="alert"
      :title="t('CORTEX_MODELS.ACTIONS.REVERT_ROUTE')"
      :description="t('CORTEX_MODELS.ROUTES.REVERT_CONFIRMATION')"
      :confirm-button-label="t('CORTEX_MODELS.ACTIONS.REVERT_ROUTE')"
      :cancel-button-label="t('CORTEX_MODELS.ACTIONS.CANCEL')"
      @confirm="confirmRouteRevert"
      @close="pendingRevert = null"
    />
  </div>
</template>
